import React, { useEffect } from 'react';

import { CredentialType } from '@/sections/asset/components/tabs/credentials/credentialForms';
import type {
  SeedStepProps,
  WizardCredentialData,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';
import { SeedTypeOption } from '@/types';

// Canonical seed-line parser (order-preserving dedupe). MUST be the same
// implementation the submit hook uses, or credential assignment desyncs from
// seed creation. Here it counts seeds for the D4 single-seed gate.
import { parseSeedLines } from './credentialAssign.util';

/**
 * Result of {@link useConfigureCredential} once the wizard is in `configure`
 * mode. `data: null` is returned instead (see the union below) while the user
 * is still on the Skip path so callers can early-`return null`.
 */
interface ConfigureCredentialState {
  data: WizardCredentialData;
  isWebService: boolean;
  isSingleSeed: boolean;
  firstSeedUrl: string;
  updateData: (patch: Partial<WizardCredentialData>) => void;
  makeSetter: <K extends keyof WizardCredentialData>(
    key: K
  ) => (action: React.SetStateAction<WizardCredentialData[K]>) => void;
}

/**
 * Shared credential-configuration plumbing for the two split wizard steps
 * (CredentialApproachStep — cards; CredentialFormStep — form). Both screens
 * operate on the same controlled `formData.credential.data` bundle, so this hook
 * centralizes the slice updaters and the gated-type snap-back effect to keep
 * them DRY.
 *
 * Returns `{ data: null }` when the wizard is NOT in `configure` mode (the user
 * chose Skip); callers should `return null` in that case. All hooks run
 * unconditionally regardless of mode (Rules of Hooks) — the early-out is on the
 * returned value, never on hook invocation.
 *
 * Secrets stay in `formData.credential.data` only — this hook holds NO local
 * secret `useState` (security P2-2; `useWizard.reset()` is the single clearing
 * point).
 */
export function useConfigureCredential(
  formData: SeedStepProps['formData'],
  setFormData: SeedStepProps['setFormData']
): ConfigureCredentialState | { data: null } {
  const isConfigure = formData.credential.mode === 'configure';
  const isWebService = formData.seedType === SeedTypeOption.WEB_SERVICE;
  const seedLines = parseSeedLines(formData.textInput);
  // COMPLEX_LOGIN and RECIPE spawn an interactive/agentic flow per seed; do not
  // fan that out across N seeds (D4). Static/Basic/Bearer/API fan out fine.
  const isSingleSeed = seedLines.length <= 1;
  const firstSeedUrl = seedLines[0] ?? '';

  // Read the configure slice if present so the effect + return value can use it
  // without re-narrowing the union in every caller.
  const data =
    formData.credential.mode === 'configure' ? formData.credential.data : null;

  // Updates a slice of the configure bundle without disturbing other per-type
  // forms. Narrows the union before writing so reset()-cleared state can't be
  // mutated into an inconsistent shape.
  const updateData = (patch: Partial<WizardCredentialData>) => {
    setFormData(prev => {
      if (prev.credential.mode !== 'configure') return prev;
      return {
        ...prev,
        credential: {
          mode: 'configure',
          data: { ...prev.credential.data, ...patch },
        },
      };
    });
  };

  // Per-type SetStateAction dispatchers the shared forms expect. Each resolves
  // the action against the current slice and threads the result back into the
  // controlled bundle (no local secret state — P2-2).
  const makeSetter =
    <K extends keyof WizardCredentialData>(key: K) =>
    (action: React.SetStateAction<WizardCredentialData[K]>) =>
      updateData({
        [key]:
          typeof action === 'function'
            ? (
                action as (
                  prev: WizardCredentialData[K]
                ) => WizardCredentialData[K]
              )(data?.[key] as WizardCredentialData[K])
            : action,
      } as Partial<WizardCredentialData>);

  // If more seeds were added on a previous step, an already-selected gated type
  // (COMPLEX_LOGIN / RECIPE) must snap back to the flow default. Done in an
  // effect (not during render) so it converges without a render-phase setState
  // on the parent wizard. Keyed only on the gating condition + flow type so it
  // runs while EITHER split step is mounted.
  const gatedSelected =
    !isSingleSeed &&
    !!data &&
    (data.selectedType === CredentialType.COMPLEX_LOGIN ||
      data.selectedType === CredentialType.RECIPE);
  useEffect(() => {
    if (gatedSelected) {
      updateData({
        selectedType: isWebService
          ? CredentialType.BASIC
          : CredentialType.STATIC_TOKEN,
      });
    }
    // updateData is recreated each render; intentionally keyed only on the
    // gating condition + flow type (exhaustive-deps is off project-wide).
  }, [gatedSelected, isWebService]);

  // Skip path: signal callers to render nothing. All hooks above already ran.
  if (!isConfigure || data === null) {
    return { data: null };
  }

  return {
    data,
    isWebService,
    isSingleSeed,
    firstSeedUrl,
    updateData,
    makeSetter,
  };
}
